import React from 'react';
import { cn } from '@rocco/utils/cn';

interface CollapsiblePageLayoutProps {
    children: React.ReactNode;
    sidePanel?: React.ReactNode;
    hideSidePanel?: boolean;
    /** When true, removes min-h-screen so the layout shrinks to fit content. */
    disableMinHeight?: boolean;
    /**
     * Horizontal padding for the outer row. Defaults to the category-page
     * gutter; pass `px-0` (etc.) to let a wrapper own the gutter so the side
     * panel can align with surrounding content (e.g. the revamp tabs).
     */
    paddingClassName?: string;
    /** Merged onto the root, e.g. for a host reordering its flex children. */
    className?: string;
}

/**
 * A layout with a main content and a side panel that can be collapsed. The
 * side panel is always hidden on mobile.
 */
export const CollapsiblePageLayout = ({
    children,
    sidePanel,
    hideSidePanel = false,
    disableMinHeight = false,
    paddingClassName = 'px-2.5 md:px-12.5',
    className,
}: CollapsiblePageLayoutProps) => {
    return (
        <div
            className={cn(
                'flex w-full md:overflow-visible',
                paddingClassName,
                !disableMinHeight && 'min-h-screen',
                className,
            )}
        >
            {/* A named region, not <aside>: this layout also mounts inside the
                detail pages' <main>, where a complementary landmark is invalid
                but a labelled region keeps the panel in the landmark list.
                Collapse only animates width/opacity, so a collapsed panel must
                also drop the landmark and leave the focus order. */}
            <div
                role={hideSidePanel ? undefined : 'region'}
                aria-label={hideSidePanel ? undefined : 'Filters'}
                inert={hideSidePanel}
                className={cn(
                    'hidden transition-all will-change-[transform,width,opacity,margin-right] md:block',
                    hideSidePanel
                        ? 'opacity-0 md:w-0 md:min-w-0 md:-translate-x-115'
                        : 'md:-mr-115 md:w-115 md:min-w-115',
                )}
                style={{
                    transitionDuration: LAYOUT_TRANSITION_DURATION,
                    transitionTimingFunction: LAYOUT_TIMING_FUNCTION,
                }}
                data-testid={'side-panel'}
            >
                {sidePanel}
            </div>
            <div
                className={cn(
                    'max-w-full grow transition-[margin-left] will-change-[margin-left]',
                    !hideSidePanel && 'md:ml-[28.75rem]',
                )}
                style={{
                    transitionDuration: LAYOUT_TRANSITION_DURATION,
                    transitionTimingFunction: LAYOUT_TIMING_FUNCTION,
                }}
            >
                {children}
            </div>
        </div>
    );
};

export const LAYOUT_TRANSITION_DURATION = `0.3s`;
export const LAYOUT_TIMING_FUNCTION = 'cubic-bezier(0.215,0.61,0.355,1)';
